import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { cantieri } from '../../utils/api';
import { formatDate } from '../../utils/date';
import FattureTabPDF from './FattureTabPDF';

const slugify = s => s.toLowerCase().replace(/[àáâãäå]/g,'a').replace(/[èéêë]/g,'e').replace(/[ìíîï]/g,'i').replace(/[òóôõö]/g,'o').replace(/[ùúûü]/g,'u').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }) : '';

const STATUS_BADGE = {
  bozza:    { label: 'Bozza',    color: 'var(--ds-muted)' },
  inviata:  { label: 'Inviata',  color: '#4A7CFF' },
  pagata:   { label: 'Saldata',  color: 'var(--ok)' },
  scaduta:  { label: 'Scadenza', color: 'var(--ds-danger)' },
  annullata:{ label: 'Annullata',color: 'var(--ds-muted)' },
};

export default function FattureTab({ data, cantiereId, onRefresh }) {
  const navigate = useNavigate();
  const fatture = data?.fatture || [];
  const milestone = data?.milestone || [];
  const proposte = data?.proposte_fattura || [];

  // R3 — conferma proposta fattura da milestone → emette la fattura bozza.
  const [confirming, setConfirming] = useState(null);
  const [proposteErr, setProposteErr] = useState('');

  const handleConfermaProposta = async (propostaId) => {
    setConfirming(propostaId);
    setProposteErr('');
    try {
      await cantieri.confermaPropostaFattura(cantiereId, propostaId);
      if (onRefresh) await onRefresh();
    } catch (e) {
      setProposteErr('Non è stato possibile emettere la fattura. Riprova.');
    } finally {
      setConfirming(null);
    }
  };

  // KPI
  // Valore contratto = valore della commessa del cantiere. La dashboard lo
  // espone in data.budget.valore_commessa (NON in data.cantiere). Nessun
  // fallback al budget del piano lavori: il "contratto" è la commessa.
  const valoreContratto = data?.budget?.valore_commessa || 0;
  // Fatturato = somma dell'IMPONIBILE delle fatture EMESSE (escluse bozze e
  // annullate). Principio piattaforma: l'economia va sull'imponibile (senza IVA),
  // coerente con la card "Fatturato" nella lista cantieri e con il valore_commessa
  // (imponibile) usato per il residuo. Prima sommava f.totale (IVA inclusa) →
  // gonfiava il fatturato e rendeva il residuo falsamente negativo.
  const fatturato = fatture.reduce(
    (s, f) => s + ((f.status !== 'annullata' && f.status !== 'bozza') ? Number(f.imponibile || 0) : 0),
    0,
  );
  const residuo = Number(valoreContratto) - fatturato;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>Fatture & SAL</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {fatture.length > 0 && (
            <PDFDownloadLink
              document={<FattureTabPDF fatture={fatture} milestone={milestone} cantiere={data?.cantiere} azienda={data?.azienda} budget={data?.budget} />}
              fileName={'fatture-sal-' + slugify(data?.cantiere?.titolo || 'cantiere') + '-' + new Date().toISOString().slice(0, 10) + '.pdf'}
            >
              {({ loading }) => <button style={btnSmall} disabled={loading}>{loading ? '...' : '📄 PDF'}</button>}
            </PDFDownloadLink>
          )}
          <button onClick={() => navigate('/fatture/nuovo')} style={btnPrimary}>+ Nuova fattura</button>
        </div>
      </div>

      {/* KPI */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1,
        background: 'var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden', marginBottom: 20,
      }}>
        <KpiBox label="VALORE CONTRATTO" value={fmtEur(valoreContratto)} color="var(--ink)" />
        <KpiBox label="FATTURATO" value={fmtEur(fatturato)} color="var(--ok)" />
        <KpiBox label="RESIDUO" value={fmtEur(residuo)} color={residuo >= 0 ? 'var(--ink)' : 'var(--ds-danger)'} />
      </div>

      {/* Proposte fattura in sospeso (R3) — visibili solo con flag attivo */}
      {proposte.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>
            🧾 Proposte fattura in sospeso
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {proposte.map(p => (
              <div key={p.id} style={{
                padding: '14px 16px', borderRadius: 'var(--radius-base)',
                border: '1px solid var(--ds-border)', borderLeft: '4px solid var(--warn)',
                background: 'var(--chip)', display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                    {p.milestone_nome}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>
                    Imponibile {fmtEur(p.imponibile)} · IVA {Number(p.aliquota_iva)}% {fmtEur(p.iva_totale)} · Totale <b style={{ color: 'var(--ink)', fontWeight: 500 }}>{fmtEur(p.totale)}</b>
                  </div>
                </div>
                <button
                  onClick={() => handleConfermaProposta(p.id)}
                  disabled={confirming === p.id}
                  style={{
                    padding: '10px 18px', borderRadius: 6, border: 'none',
                    background: 'var(--brand)', color: '#fff', cursor: confirming === p.id ? 'default' : 'pointer',
                    fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', opacity: confirming === p.id ? 0.6 : 1,
                  }}
                >
                  {confirming === p.id ? 'Emissione…' : 'Conferma ed emetti'}
                </button>
              </div>
            ))}
          </div>
          {proposteErr && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ds-danger)' }}>{proposteErr}</div>
          )}
        </div>
      )}

      {/* Lista fatture */}
      {fatture.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>
          Nessuna fattura per questo cantiere.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 24 }}>
          {fatture.map(f => {
            const badge = STATUS_BADGE[f.status] || STATUS_BADGE.bozza;
            const isPagata = f.status === 'pagata';
            const isScaduta = f.status === 'scaduta';
            return (
              <div key={f.id} onClick={() => navigate('/fatture')} style={{
                padding: '14px 16px', borderRadius: 'var(--radius-base)', cursor: 'pointer',
                border: '1px solid var(--ds-border)',
                borderLeft: '4px solid ' + (isPagata ? 'var(--ok)' : isScaduta ? 'var(--ds-danger)' : 'var(--ds-border)'),
                background: 'var(--chip)', display: 'flex', alignItems: 'center', gap: 14,
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--ds-bg)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--chip)'}
              >
                <div style={{ fontSize: 20, flexShrink: 0 }}>🧾</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                    {f.numero} {f.tipo_display ? '· ' + f.tipo_display : ''}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>
                    {f.data_emissione && ('Emessa ' + fmtDate(f.data_emissione))}
                    {f.data_pagamento && (' · Pagata ' + fmtDate(f.data_pagamento))}
                    {!f.data_pagamento && f.data_scadenza && (' · ')}
                    {isScaduta && <span style={{ color: 'var(--ds-danger)' }}>⚠️ Scadenza {fmtDate(f.data_scadenza)}</span>}
                    {!isScaduta && !f.data_pagamento && f.data_scadenza && ('Scadenza ' + fmtDate(f.data_scadenza))}
                    {' · '}
                    <span style={{ color: badge.color, fontWeight: 500 }}>
                      {isPagata ? '✅ ' : ''}{badge.label}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 500, color: isPagata ? 'var(--ok)' : 'var(--ink)', whiteSpace: 'nowrap' }}>
                  {fmtEur(f.imponibile)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SAL finale suggerito */}
      {residuo > 0 && fatture.length > 0 && (
        <div style={{
          padding: '14px 16px', borderRadius: 'var(--radius-base)',
          border: '1px dashed var(--ds-border)', background: 'var(--chip)',
          textAlign: 'center', fontSize: 13, color: 'var(--ds-muted)',
        }}>
          🏁 SAL finale — {fmtEur(residuo)} · da emettere a fine lavori
        </div>
      )}

      {/* Milestone / SAL */}
      {milestone.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>🏁 SAL / Milestone</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {milestone.map(m => (
              <div key={m.id} style={{
                padding: '12px 16px', borderRadius: 'var(--radius-base)',
                border: '1px solid var(--ds-border)',
                borderLeft: '4px solid ' + (m.raggiunta ? 'var(--ok)' : 'var(--warn)'),
                background: 'var(--chip)', display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 18 }}>{m.raggiunta ? '✅' : '⏳'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{m.nome}</div>
                  <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>{formatDate(m.data)}</div>
                </div>
                {m.importo_sal && (
                  <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)' }}>
                    {fmtEur(m.importo_sal)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiBox({ label, value, color }) {
  return (
    <div style={{ padding: '16px 20px', background: 'var(--chip)' }}>
      <div style={{ fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color, fontFamily: 'var(--font-display)' }}>{value}</div>
    </div>
  );
}

const btnPrimary = { padding: '10px 20px', borderRadius: 6, border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnSmall = { padding: '6px 14px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 12 };
